/**
 * <s-top-bar> — announcement bar above the header (snippets/top-bar.liquid).
 *
 * Rotates its [data-slide] children every data-interval ms, pausing while hovered, focused or the tab is hidden.
 * Users who prefer reduced motion see the slides change without the slide animation.
 * Ticks the [data-countdown] units (d/h/m/s) once a second towards data-ends and removes itself when the time is up.
 */
class STopBar extends HTMLElement {
  #ac;
  #rotateTimer = 0;
  #tickTimer = 0;
  #index = 0;
  #paused = false;

  connectedCallback() {
    this.ends = Date.parse(this.dataset.ends ?? '');
    if (Number.isFinite(this.ends) && Date.now() >= this.ends) {
      this.remove();
      return;
    }

    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.units = {};
    for (const el of this.querySelectorAll('[data-unit]')) this.units[el.getAttribute('data-unit')] = el;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.addEventListener('pointerenter', this.#pause, opts);
    this.addEventListener('pointerleave', this.#resume, opts);
    this.addEventListener('focusin', this.#pause, opts);
    this.addEventListener('focusout', this.#onFocusOut, opts);
    document.addEventListener('visibilitychange', this.#onVisibility, opts);

    this.#tick();
    this.#tickTimer = window.setInterval(this.#tick, 1000);
    this.#schedule();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    window.clearInterval(this.#tickTimer);
    window.clearTimeout(this.#rotateTimer);
  }

  #schedule() {
    window.clearTimeout(this.#rotateTimer);
    if (this.#paused || this.slides.length < 2) return;
    const interval = Number(this.dataset.interval) || 5000;
    this.#rotateTimer = window.setTimeout(() => {
      this.#show((this.#index + 1) % this.slides.length);
      this.#schedule();
    }, interval);
  }

  /** @param {number} next */
  #show(next) {
    this.slides.forEach((slide, i) => {
      const active = i === next;
      slide.toggleAttribute('data-active', active);
      slide.toggleAttribute('inert', !active);
      if (active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });
    this.#index = next;
  }

  #pause = () => {
    this.#paused = true;
    window.clearTimeout(this.#rotateTimer);
  };

  #resume = () => {
    if (this.contains(document.activeElement)) return;
    this.#paused = false;
    this.#schedule();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#paused = false;
    this.#schedule();
  };

  #onVisibility = () => {
    if (document.hidden) this.#pause();
    else this.#resume();
  };

  #tick = () => {
    const left = Math.max(0, this.ends - Date.now());
    if (left <= 0) {
      this.remove();
      return;
    }
    const total = Math.floor(left / 1000);
    const parts = {
      d: Math.floor(total / 86400),
      h: Math.floor((total % 86400) / 3600),
      m: Math.floor((total % 3600) / 60),
      s: total % 60,
    };
    for (const [key, value] of Object.entries(parts)) {
      const el = this.units[key];
      if (el) el.textContent = String(value).padStart(2, '0');
    }
  };
}

if (!customElements.get('s-top-bar')) customElements.define('s-top-bar', STopBar);
